import { useContext, useMemo } from "react";
import { ShellContext } from "../context";
import { DataSubscription } from "../model";
import { useOwnerModule } from "./useOwnerModule";

/**
 * useDataChannel signature function.
 */
export interface UseDataChannel {
  /**
   * Returns the data subscribed to the channel with the specified ID.
   * @param id The content channel ID.
   */
  <T = unknown>(id: string): ReadonlyArray<DataSubscription<T>>;

  /**
   * Returns the data subscribed to the channel with the module and channel ID.
   * @param moduleId The module ID to get channels for.
   * @param channelId The content channel ID.
   */
  <T = unknown>(moduleId: string | null, channelId: string): ReadonlyArray<DataSubscription<T>>;
}

/**
 * Returns the data subscribed to the content channel with the specified ID.
 * @param id The content channel ID.
 */
export const useDataChannel: UseDataChannel = <T = unknown>(...args: [string | null, string?]): ReadonlyArray<DataSubscription<T>> => {
  let moduleId: string | null;
  let channelId: string;

  const ownerModule = useOwnerModule();

  if (args.length === 1) {
    moduleId = ownerModule ? ownerModule.id : null;

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    channelId = args[0]!;
  } else {
    moduleId = args[0];

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    channelId = args[1]!;
  }

  const { getChannelSubscriptions } = useContext(ShellContext);

  const channelSubscriptions = useMemo(() => {
    return getChannelSubscriptions(moduleId, channelId).filter((subscription) => subscription.type === "data") as ReadonlyArray<
      DataSubscription<T>
    >;
  }, [getChannelSubscriptions, moduleId, channelId]);

  return channelSubscriptions;
};
